import React, { useState, useEffect } from 'react';
import { Video, Upload, Trash2, Eye, CheckCircle, XCircle, Loader, RefreshCw } from 'lucide-react';
import { siteVideoService, SiteVideoListItem } from '../../../services/siteVideoService';
import { SiteVideoUploadModal } from '../SiteVideoUploadModal';

// Predefined page locations for site videos
const SITE_VIDEO_PAGES = [
  { key: 'homepage_hero', name: 'Homepage Hero' },
  { key: 'pem_hero', name: 'Prompt Engineering Mastery' },
  { key: 'aita_hero', name: 'AI Tool Accelerator' },
  { key: 'aieb_hero', name: 'AI Essentials Bundle' },
];

export const SiteVideoManager: React.FC = () => {
  const [videos, setVideos] = useState<Record<string, SiteVideoListItem>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState<{ key: string; name: string } | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  // Load videos on mount
  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await siteVideoService.getAllSiteVideos();

      // Convert array to keyed object
      const videosMap: Record<string, SiteVideoListItem> = {};
      response.videos.forEach((video) => {
        videosMap[video.page_key] = video;
      });

      setVideos(videosMap);
    } catch (err: any) {
      console.error('Error loading site videos:', err);
      setError(err.message || 'Failed to load site videos');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = (page: { key: string; name: string }) => {
    setSelectedPage(page);
    setUploadModalOpen(true);
  };

  const handleUploadComplete = () => {
    loadVideos();
  };

  const handleDelete = async (pageKey: string, pageName: string) => {
    if (!confirm(`Are you sure you want to delete the video for ${pageName}?`)) {
      return;
    }

    try {
      await siteVideoService.deleteSiteVideo(pageKey);
      loadVideos();
    } catch (err: any) {
      console.error('Error deleting video:', err);
      alert(err.message || 'Failed to delete video');
    }
  };

  const handlePreview = async (pageKey: string) => {
    try {
      const videoData = await siteVideoService.getSiteVideo(pageKey);
      if (videoData.playback_url) {
        setPreviewUrl(videoData.playback_url);
        setPreviewModalOpen(true);
      } else {
        alert('No playback URL available for this video');
      }
    } catch (err: any) {
      console.error('Error loading preview:', err);
      alert(err.message || 'Failed to load video preview');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Site Videos</h2>
          <p className="text-gray-400 mt-1">Manage promotional videos for homepage and sales pages</p>
        </div>
        <button
          onClick={loadVideos}
          className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg flex items-center space-x-2 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Video cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {SITE_VIDEO_PAGES.map((page) => {
          const video = videos[page.key];
          const hasVideo = !!video && !!video.video_url;

          return (
            <div
              key={page.key}
              className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-colors"
            >
              {/* Card header */}
              <div className="p-6 border-b border-gray-800">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg">
                      <Video className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{page.name}</h3>
                      <p className="text-xs text-gray-500 mt-1 font-mono">{page.key}</p>
                    </div>
                  </div>
                  <div>
                    {hasVideo ? (
                      <div className="flex items-center space-x-1 text-green-400 bg-green-500/10 px-3 py-1 rounded-full">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-xs font-medium">Uploaded</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1 text-gray-400 bg-gray-800 px-3 py-1 rounded-full">
                        <XCircle className="w-4 h-4" />
                        <span className="text-xs font-medium">No Video</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Card content */}
              <div className="p-6">
                {hasVideo && video ? (
                  <div className="space-y-4">
                    {/* Video info */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">File Size</p>
                        <p className="text-white font-medium">
                          {video.file_size ? siteVideoService.formatFileSize(video.file_size) : '--'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Duration</p>
                        <p className="text-white font-medium">
                          {siteVideoService.formatDuration(video.duration)}
                        </p>
                      </div>
                    </div>

                    {video.uploaded_at && (
                      <div className="text-xs text-gray-500">
                        Uploaded {new Date(video.uploaded_at).toLocaleString()}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePreview(page.key)}
                        className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg flex items-center justify-center space-x-2 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        <span>Preview</span>
                      </button>
                      <button
                        onClick={() => handleUploadClick(page)}
                        className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center justify-center space-x-2 transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        <span>Replace</span>
                      </button>
                      <button
                        onClick={() => handleDelete(page.key, page.name)}
                        className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg flex items-center justify-center transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Upload className="w-8 h-8 text-gray-600" />
                    </div>
                    <p className="text-gray-400 mb-4">No video uploaded yet</p>
                    <button
                      onClick={() => handleUploadClick(page)}
                      className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-medium hover:from-blue-600 hover:to-purple-600 transition-all"
                    >
                      Upload Video
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Upload Modal */}
      {selectedPage && (
        <SiteVideoUploadModal
          isOpen={uploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false);
            setSelectedPage(null);
          }}
          pageKey={selectedPage.key}
          pageName={selectedPage.name}
          existingVideo={
            videos[selectedPage.key]
              ? {
                  url: videos[selectedPage.key].video_url || '',
                  file_size: videos[selectedPage.key].file_size,
                  duration: videos[selectedPage.key].duration,
                }
              : undefined
          }
          onUploadComplete={handleUploadComplete}
        />
      )}

      {/* Preview Modal */}
      {previewModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl border border-gray-800 max-w-4xl w-full shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <h3 className="text-xl font-semibold text-white">Video Preview</h3>
              <button
                onClick={() => {
                  setPreviewModalOpen(false);
                  setPreviewUrl('');
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <video
                src={previewUrl}
                controls
                autoPlay
                className="w-full rounded-lg"
                style={{ maxHeight: '70vh' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
